import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { animated, useSpring } from '@react-spring/web';
import moment from 'moment-timezone';
import update from 'immutability-helper';
import { useDataStore } from 'files/StateManagement';

import Abstract from 'classes/Abstract.js';
import ActionSteps from 'views/ActionSteps.js';
import AltFieldMapper, { validateRequiredFields } from 'views/AltFieldMapper.js';
import AnimatedElement from 'views/AnimatedElement.js';
import Appearance from 'styles/Appearance.js';
import AskRequiem from 'views/AskRequiem.js';
import { AsyncInteractionItem } from 'managers/Components.js';
import Button from 'views/Button.js';
import DataVaultItem from 'classes/DataVaultItem.js';
import { DndProvider } from 'react-dnd';
import FieldMapper from 'views/FieldMapper';
import Layer, { LayerItem } from 'structure/Layer.js';
import LottieView from 'views/Lottie.js';
import { MultiBackend, Tree, getBackendOptions } from '@minoru/react-dnd-treeview';
import { PanelContentContainer } from 'structure/Panel.js';
import Request from 'files/Request.js';
import TextField from 'views/TextField.js';
import TextView from 'views/TextView.js';
import Utils from 'files/Utils.js';

export const AddEditDataVaultItem = ({ editable = true, fileTarget = null, isNewTarget }, { abstract, index, options, utils }) => {

    const resource = {
        category: 'data_vault_items',
        id: isNewTarget ? 'new_data_vault_item' : `data_vault_items.${abstract.getID()}`,
        target: abstract.object,
        title: isNewTarget ? 'Upload to Data Vault' : `Edit Your Data Vault File`
    }
    const uploadController = useRef(null);
    
    const [creditsMetadata, setCreditsMetadata] = useState(null);
    const [edits, setEdits] = useState({});
    const [file, setFile] = useState(null);
    const [hasMadeEdits, setHasMadeEdits] = useState(false);
    const [hasMadeRequiemAIEdits, setHasMadeRequiemAIEdits] = useState(false);
    const isRequiemEnabled = true;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [progressBarAnimations, progressBarAnimationsAPI] = useSpring(() => ({
        config: { mass: 1, tension: 180, friction: 16 },
        width: '0%',
    }));
    const { requiem, setRequiem } = useDataStore();
    const [sessionId, setSessionId] = useState(null);
    const [viewState, setViewState] = useState('configure');

    const onCloseClick = () => {

        // no further logic is required if no edits have been made
        if(editable === true && hasMadeEdits === false) {
            setLayerState('close');
            requiem.functions.smart_flow.cancel(resource);
            return;
        }

        // show alert requesting confirmation to close the layer without saving changes
        utils.alert.show({
            title: 'Unsaved Changes',
            message: `It looks like you have some unsaved changes. Would you like to save these changes before closing this window?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Do Not Save Changes',
                style: 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onSubmitClick, 500);
                    return;
                }

                // close layer and cancel requiem form
                setLayerState('close');
                requiem.functions.smart_flow.cancel(resource);
            }
        });
    }

    const onContinueClick = () => {
        
        // determine if any edits were made that require credits estimation
        if(isNewTarget === true || (hasMadeRequiemAIEdits === true && edits.ai_enabled === true)) {
            onPrepareCreditsEstimateClick();
            return;
        }

        // fallback to standard updates for metadata
        onSubmitClick();
    }

    const onDeleteItem = async () => {
        return new Promise((resolve, reject) => {

            // prepare confirmation function
            const onConfirm = async () => {
                try {

                    // submit request to server
                    await Request.post(utils, '/data_vault/', {
                        id: abstract.getID(),
                        type: 'delete'
                    });

                    // notify subscribers of data change
                    utils.content.fetch('data_vault_items');

                    // end loading and notify origin that promise has been resolved
                    resolve({ 
                        message: `${abstract.object.name} has been removed from your Secure Data Vault.`,
                        onClick: setLayerState.bind(this, 'close') 
                    });
        
                } catch(e) {
                    let error = new Error(`There was an issue deleting this item from your Data Vault. ${e.message || 'An unknown error occurred'}`);
                    reject(error);
                }
            }

            // show alert requesting user confirmation
            utils.alert.show({
                title: 'Delete Item',
                message: `Are you sure that you want to delete "${abstract.object.name}" from your Data Vault? This can not be undone.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'destructive'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'confirm') {
                        setTimeout(onConfirm, 500);
                        return;
                    }
                    let error = new Error('user_cancelled');
                    reject(error);
                }
            });
        });
    }

    const onDownloadItem = async () => {
        return new Promise(async (resolve, reject) => {
            try {

                // submit request to server
                let { url } = await Request.get(utils, '/data_vault/', {
                    id: abstract.getID(),
                    type: 'download'
                });

                // end loading and notify origin that promise has been resolved
                resolve({ 
                    buttons: [{
                        key: 'download',
                        title: 'Download',
                        style: 'default'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'cancel'
                    }],
                    message: `Your secure link for ${abstract.object.name} has been prepared and is ready for download.`,
                    onClick: key => {
                        if(key === 'download') {
                            window.open(url);
                            return;
                        }
                    }
                });
    
            } catch(e) {
                let error = new Error(`There was an issue preparing your secure download link. ${e.message || 'An unknown error occurred'}`);
                reject(error);
            }
        });
    }

    const onPrepareCreditsEstimateClick = async () => {
        try {

           // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // fetch or optionally request user ai preference
            let aiEnabled = await getUserAIPreference();

            // reset value for progress bar before submitting upload request
            progressBarAnimationsAPI.start({ width: `0%` }, { immediate: true });
            
            // set view state to the upload screen
            setViewState('upload');

            // create abort controller for cancellation
            uploadController.current = new AbortController();

            // upload file to temporary location for credits estimation
            await Utils.sleep(0.5);
            let { credits_metadata, session_id } = await Request.upload(utils, '/data_vault/', {
                ...edits,
                ai_enabled: aiEnabled,
                type: 'new_session'
            },{
                files: [{
                    data: file.raw,
                    key: 'file'
                }],
                signal: uploadController.current.signal,
                onProgressChange: evt => {
                    progressBarAnimationsAPI.start({ width: `${evt.progress || 0}%` }, { immediate: true });
                }
            });

            // clear the upload controller since upload completed successfully
            uploadController.current = null;

            // update local state with estimate result and new view state
            await Utils.sleep(0.5);
            setSessionId(session_id);
            setCreditsMetadata(credits_metadata);
            setViewState('checkout');

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue uploading your file. ${e.message || 'An unknown error occurred'}`,
                onClick: setViewState.bind(this, 'configure'),
                origin: 'add_data_vault_item.on_prepare_credits_estimate_click'
            });
        }
    }

    const onRequiemCollaborateClick = () => {
        utils.layer.open({
            abstract: abstract,
            Component: RequiemDataVaultItemEditor,
            id: `requiem_data_vault_item_editor_${abstract.getID()}`,
        });
    }

    const onRequiemCreditsSupportClick = () => {
        setRequiem(requiem => ({ 
            ...requiem,
            request: {
                context: creditsMetadata,
                preset: 'data_vault_item_credits_support'
            }
        }));
    }

    const onSubmitClick = async () => {
        try {

            // verify that required fields were filled out
            await validateRequiredFields(getFields);

            // set loading flag
            setLoading('submit');

            // submit request to server as a file upload if target is new
            if(isNewTarget === true) {
                await Request.post(utils, '/data_vault/', {
                    session_id: sessionId,
                    type: 'complete_session'
                });
            }

            // submit standard post request if target is not new
            if(isNewTarget === false) {
                await Request.post(utils, '/data_vault/', {
                    ...edits,
                    id: abstract.getID(),
                    type: 'update'
                });
            }

            // notify subscribers that new data is available
            utils.content.fetch('data_vault_items');

            // notify requiem that a resource interaction has been completed
            requiem.functions.smart_flow.complete(resource);

            // show confirmation banner and close layer for existing targets
            if(isNewTarget === false) {
                utils.banner.show({
                    title: 'Secure Data Vault Updated',
                    message: `Your file "${edits.name}" has been updated and changes are now live in your Secure Data Vault.`,
                    onShow: setLayerState.bind(this, 'close')
                });
                return;
            }

            // end loading and update view state to confirmation screen
            setLoading(false);
            setViewState('upload_confirmation');

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue ${isNewTarget ? 'creating' : 'updating'} your Secure Data Vault. ${e.message || 'An unknown error occurred'}`,
                origin: 'add_edit_data_vault_item.on_submit_click'
            });
        }
    }

    const onUpdateTarget = (props, options = {}) => {
        let { hasMadeRequiemAIEdits = false } = options;
        setHasMadeEdits(true);
        setHasMadeRequiemAIEdits(hasMadeRequiemAIEdits);
        setEdits({ ...edits, ...props });
    }

    const getAccessManagementComponents = () => {
        if(abstract.object.metadata?.is_company_owned === true) {
            return (
                <LayerItem 
                collapsed={false}
                title={'Resources'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        <AsyncInteractionItem 
                        description={'This will securely download a copy of this item from your Secure Data Vault.'}
                        image={'images/download-data-vault-item.png'}
                        lastItem={true}
                        onAsyncClick={onDownloadItem}
                        title={'Download Copy'}
                        utils={utils} />
                    </div>
                </LayerItem>
            )
        }
        return isNewTarget === false && (
            <LayerItem 
            collapsed={false}
            title={'Access Management'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {editable === true && abstract.object.ai_enabled === true && (
                        <AsyncInteractionItem 
                        description={'Collaborate with Requiem to ensure it understands the contents and your intentions for this file.'}
                        image={'images/requiem-ai-yellow.png'}
                        onAsyncClick={onRequiemCollaborateClick}
                        title={'Collaborate with Requiem AI'}
                        utils={utils} />
                    )}
                    {editable === true && (
                        <AsyncInteractionItem 
                        description={'This will permanently delete the item from your Secure Data Vault. This can not be undone.'}
                        image={'images/delete-red-small.png'}
                        onAsyncClick={onDeleteItem}
                        title={'Delete from Vault'}
                        utils={utils} />
                    )}
                    <AsyncInteractionItem 
                    description={'This will securely download a copy of this item from your Secure Data Vault.'}
                    image={'images/download-data-vault-item.png'}
                    lastItem={true}
                    onAsyncClick={onDownloadItem}
                    title={'Download Copy'}
                    utils={utils} />
                </div>
            </LayerItem>
        )
    }

    const getButtons = () => {

        // determine if file is company owned
        if(abstract.object.metadata?.is_company_owned === true) {
            return [{
                color: 'dark',
                id: 'close',
                onClick: setLayerState.bind(this, 'close'),
                text: 'Close'
            }];
        }
        
        // show single close button for confirmation state
        if(viewState === 'upload_confirmation') {
            return [{
                color: 'dark',
                id: 'close',
                onClick: setLayerState.bind(this, 'close'),
                text: 'Close'
            }];
        }

        // determine if view state is the checkout screen
        if(viewState === 'checkout') {
            return [{
                color: 'dark',
                id: 'back',
                onClick: setViewState.bind(this, 'configure'),
                text: 'Back'
            },{
                color: 'primary',
                id: 'submit',
                loading: loading === 'submit',
                onClick: onSubmitClick,
                text: creditsMetadata.cost > 0 ? 'Submit Order' : 'Submit'
            }];
        }

        // determine if view state for upload if active
        if(viewState === 'upload') {
            return [{
                color: 'dark',
                id: 'back',
                onClick: () => {
                    uploadController.current?.abort();
                    setViewState('configure');
                },
                text: 'Back'
            }];
        }

        return [{
            color: 'dark',
            id: 'close',
            onClick: onCloseClick,
            text: 'Close'
        },{
            color: 'primary',
            id: 'continue',
            loading: loading === 'submit',
            onClick: onContinueClick,
            text: file || hasMadeRequiemAIEdits ? 'Continue' : 'Save Changes',
            visible: file || hasMadeEdits || hasMadeRequiemAIEdits ? true : false
        }];
    }

    const getCheckoutOverview = () => {
        return (
            <LayerItem
            collapsed={false}
            title={'About Your Upload'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    overflow: 'visible',
                    padding: 16,
                    paddingTop: 12,
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block',
                        marginBottom: 24
                    }}>{`To complete your upload, we’ve calculated the number of credits required for your item based on its size and processing needs. If a cost is associated, you’ll be prompted to purchase the necessary credits before continuing. Credits are simply the way you pay for storage and analysis within Requiem, and they ensure your item can be fully processed without interruption. If you have any questions at all, Requiem is here to help.`}</span>
                    <AskRequiem onClick={onRequiemCreditsSupportClick} />
                </div>
            </LayerItem>
        )
    }
    
    const getCompanyOwnedFields = () => {
        return [{
            collapsed: false,
            lastItem: false,
            title: 'Details',
            items: [{
                key: 'last_modified',
                title: 'Last Updated',
                value: Utils.format.date(abstract.object.last_modified || abstract.object.date),
            },{
                key: 'name',
                title: 'Name',
                value: abstract.object.name
            }]
        }];
    }

    const getContent = () => {

        // determine if file is company owned
        if(abstract.object.metadata?.is_company_owned === true) {
            return (
                <>
                <FieldMapper
                sections={getCompanyOwnedFields()}
                utils={utils} />
                <LayerItem
                collapsed={false}
                lastItem={false}
                title={'Description'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.defaultText(),
                            display: 'block'
                        }}>{abstract.object.metadata?.description || 'No description available'}</span>
                    </div>
                </LayerItem>
                {getAccessManagementComponents()}
                <span style={{
                    color: Appearance.colors.subText(),
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.5,
                    marginTop: 24,
                    width: '100%'
                }}>{`This document and its contents are the exclusive property of LifeWipe and are intended for authorized internal use only. All information contained herein is confidential and may include proprietary, strategic, or sensitive business material. Unauthorized access, distribution, duplication, modification, or disclosure of this document — in whole or in part — is prohibited. This document may not be shared externally, copied for non-business purposes, or used outside the scope of approved LifeWipe activities without prior written authorization. Possession or access to this document does not grant any rights to its contents beyond permitted internal use.`}</span>
                </>
            )
        }

        // determine if view state is for upload progress
        if(viewState === 'upload_confirmation') {
            return (
                <>
                <LayerItem
                collapsed={false}
                lastItem={false}
                title={'Data Vault Upload Complete'}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        padding: '12px 16px 12px 16px',
                        width: '100%'
                    }}>
                        <span style={{
                            ...Appearance.textStyles.defaultText()
                        }}>
                            <b>{`🎉 Congratulations!`}</b>
                            {` LifeWipe has encrypted and saved this file in your Secure Data Vault, ensuring it remains protected and untouched unless you choose otherwise. You can decide how this file should be handled in the future, add access rules, or simply leave it stored safely for peace of mind.`}
                        </span>
                    </div>
                </LayerItem>
                <ActionSteps
                exclude={['create_data_vault_item']}
                onClick={setLayerState.bind(this, 'close')}
                utils={utils} />
                </>
            )
        }

        // determine if view state is for checkout
        if(viewState === 'checkout') {
            return (
                <>
                {getCheckoutOverview()}
                {getOrderOverview()}
                </>
            )
        }

        // determine if view state is for content upload
        if(viewState === 'upload') {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minHeight: 125,
                    padding: 24,
                    textAlign: 'center',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.layerItemTitle(),
                        fontFamily: 'Zen Dots'
                    }}>{'Uploading your file...'.toUpperCase()}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        marginTop: 6
                    }}>{`This may take a while depending on the speed of your internet connection. Please be patient while we process your file.`}</span>
                    <div style={{
                        alignItems: 'center',
                        backgroundColor: Appearance.colors.divider(),
                        border: `1px solid ${Appearance.colors.softBorder()}`,
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',   
                        height: 16,
                        padding: 8,
                        marginTop: 16,
                        width: '100%'
                    }}>
                        <animated.div style={{
                            backgroundColor: Appearance.colors.primary(),
                            borderRadius: 3,
                            height: 6,
                            ...progressBarAnimations
                        }} />
                    </div>
                </div>
            )
        }

        // fallback to returning config components
        return (
            <>
            <AltFieldMapper
            fields={getFields()}
            resourceID={resource.id}
            utils={utils} />
            {getAccessManagementComponents()}
            <span style={{
                color: Appearance.colors.subText(),
                display: 'block',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.5,
                marginTop: 24,
                width: '100%'
            }}>{`By uploading content to the Secure Data Vault, you confirm that you have the legal right to store and manage the materials submitted. LifeWipe is not responsible for verifying ownership, accuracy, or legality of uploaded documents, and does not provide legal advice regarding their use. Users are solely responsible for ensuring that no copyrighted, unlawful, or harmful content is stored within the vault.`}</span>
            </>
        )
    }

    const getFields = () => {
        return [{
            collapsed: false,
            description: `Use this section to securely add a file to your Data Vault—whether it's a will, insurance policy, or personal record. Simply select a file from your device to encrypt and store it within your account. You'll be able to label, organize, and apply custom rules to each document after upload.`,
            title: 'Upload to Data Vault',
            visible: isNewTarget === true,
            items: [{
                component: 'file_picker',
                description: 'This is a file selected from your device that you would like to add to your data vault.',
                key: 'file',
                onChange: file => {
                    setFile(file);
                    setEdits(edits => ({ ...edits, name: file.file_name }));
                    setHasMadeEdits(true);
                },
                props: {
                    fileTypes: ['csv', 'doc', 'docx', 'video', 'image', 'pdf', 'txt', 'xls', 'xlsx', 'zip']
                },
                requiemSmartFlow: true,
                title: 'Choose A File',
                value: file
            }]
        },{
            collapsed: false,
            description: 'Add a name and brief description to help LifeWipe understand purpose and context. Providing clear, thoughtful labels ensures your content is well organized and easily accessible.',
            lastItem: false,
            title: 'About This File',
            items: [{
                component: 'textview',
                description: 'This description helps provide context about the contents and purpose of this file within your Secure Data Vault.',
                key: 'description',
                onChange: text => onUpdateTarget({ description: text }),
                requiemSmartFlow: true,
                title: 'Description',
                value: edits.description
            },{
                component: 'textfield',
                description: 'This is the name that will be used to identify this file within your Secure Data Vault.',
                key: 'name',
                onChange: text => onUpdateTarget({ name: text }),
                requiemSmartFlow: true,
                title: 'Name',
                value: edits.name
            }]
        },{
            collapsed: false,
            description: 'Enabling Requiem activates LifeWipe’s secure learning protocol, allowing the system to study the files you upload, understand how you communicate, and begin forming a digital representation of your voice, tone, and intent. This helps trusted contacts locate crucial information without confusion or delay. Requiem also marks the beginning of your digital self—one designed to protect and speak on your behalf when you no longer can.',
            key: 'requiem',
            lastItem: isNewTarget === true,
            title: 'Requiem AI',
            visible: isRequiemEnabled ? true : false,
            items: [{
                component: 'bool_list',
                description: 'Enable this option to allow Requiem to access the contents of this file and analyze it to help build your digital self',
                key: 'ai_enabled',
                onChange: val => onUpdateTarget({ ai_enabled: val }, { hasMadeRequiemAIEdits: true }),
                requiemSmartFlow: true,
                title: 'Enable',
                value: edits.ai_enabled || false
            },{
                component: 'bool_list',
                description: `If this file includes your words—spoken or written—we can analyze it to help shape your digital voice and tone for future messages. Skip if it's just a photo or not written in your voice.`,
                key: 'analyze_communication_style',
                onChange: val => onUpdateTarget({ analyze_communication_style: val }, { hasMadeRequiemAIEdits: true }),
                requiemSmartFlow: true,
                required: false,
                title: 'Analyze Communication Style',
                value: edits.analyze_communication_style || false,
                visible: edits.ai_enabled === true
            }]
        }];
    }

    const getOrderOverview = () => {
        return viewState === 'checkout' && (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={creditsMetadata?.cost > 0 ? 'Order Overview' : 'Credits Overview'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    width: '100%'
                }}>
                    {getOrderOverviewProps()}
                </div>
                {true && (
                    <span style={{
                        color: Appearance.colors.subText(),
                        display: 'flex',
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 1.75,
                        marginTop: 24,
                        textAlign: 'left',
                        width: '100%'
                    }}>{`By proceeding with this one-time credit purchase, you acknowledge that the required credit amount of ${Utils.toCurrency(creditsMetadata.cost)} has been calculated based on the size and characteristics of the item you are uploading to the Secure Data Vault. Credits are used exclusively to cover platform processing and storage costs and are non-refundable once the purchase is completed. If you have any questions before completing your purchase, Requiem is available to help clarify any details.`}</span>
                )}
            </LayerItem>
        )
    }

    const getOrderOverviewProps = () => {
    
        // prepare billing items with dynamic costs
        let items = [{
            key: 'current_balance',
            title: 'Current Credits Balance',
            value: Utils.format.number(creditsMetadata.current_balance)
        },{
            key: 'storage_credits',
            title: 'Credits Required for Storage',
            value: Utils.format.number(creditsMetadata.storage_credits)
        },{
            key: 'ai_processing_credits',
            title: 'Credits Required for Requiem Processing',
            value: Utils.format.number(creditsMetadata.ai_processing_credits),
            visible: creditsMetadata.ai_processing_credits > 0
        },{
            key: 'calculated_credits',
            title: 'Total Credits Required',
            value: Utils.format.number(creditsMetadata.calculated_credits)
        },{
            color: Appearance.colors.red,
            key: 'purchase_credits',
            title: 'Additional Credits Needed',
            value: Utils.format.number(creditsMetadata.purchase_credits),
            visible: creditsMetadata.purchase_credits > 0
        },{
            key: 'remaining_credits_balance',
            title: 'Remaining Credits After Upload',
            value: Utils.format.number(creditsMetadata.remaining_credits_balance),
            visible: creditsMetadata.remaining_credits_balance >= 0
        },{
            color: creditsMetadata.cost > 0 ? Appearance.colors.green : null,
            key: 'total',
            title: 'Purchase Cost',
            value: Utils.toCurrency(creditsMetadata.cost || 0)
        }];

        return items.filter(item => {
            return item.visible !== false;
        }).map((item, index, items) => {
            return (
                <div 
                key={index}
                style={{
                    alignItems: 'center',
                    borderBottom: index !== items.length - 1 && Appearance.styles.bottomBorder(),
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    padding: 12,
                    textAlign: 'left',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultTextSmall(),
                    }}>{item.title}</span>
                    <span style={{
                        ...Appearance.textStyles.defaultTextSmall(),
                        ...item.color && {
                            color: item.color
                        }
                    }}>{item.value}</span>
                </div>
            )
        });
    }

    const getSupportConfig = () => {
        if(viewState !== 'configure') {
            return null;
        }
        if(abstract.object.metadata?.is_company_owned === true) {
            return null;
        }
        if(isNewTarget === false) {
            return {
                description: 'Need help? Activate Requiem AI for real-time guidance as you manage your file. Requiem can assist with any questions or concerns you may have during this process.',
                requiem: {
                    enabled: true,
                    smart_flow: {
                        items: getFields(),
                        ...resource
                    } 
                },
                title: 'Secure Data Vault'
            }
        }
        return {
            description: 'Need help? Activate Requiem AI for real-time guidance as you upload your file to LifeWipe. Requiem can assist with any questions or concerns you may have during this process.',
            requiem: {
                enabled: true,
                smart_flow: {
                    items: getFields(),
                    ...resource
                } 
            },
            title: 'Secure Data Vault'
        }
    }

    const getUserAIPreference = async () => {
        return new Promise(resolve => {

            // return value as-is if already enabled
            if(edits.ai_enabled === true) {
                return resolve(true);
            }

            // no further action is required if user has opted out of this prompt
            let value = localStorage.getItem('data_vault_ai_alert_opt_out');
            if(value) {
                return resolve(false);
            }

            // request confirmation from user to enable requiem
            utils.alert.show({
                title: 'Use Requiem AI',
                message: `Would you like to enable Requiem AI for this file? Enabling Requiem allows the system to analyze the contents of your file to help build your digital self and ensure your wishes are accurately represented.`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'default'
                },{
                    key: 'no',
                    title: 'No',
                    style: 'cancel'
                },{
                    key: 'dont_remind',
                    title: `Don't Remind Me Again`,
                    style: 'destructive'
                }],
                onClick: key => {
                    if(key === 'dont_remind') {
                        localStorage.setItem('data_vault_ai_alert_opt_out', moment().format('YYYY-MM-DD HH:mm:ss'));
                    }
                    resolve(key === 'confirm');
                }
            });
        });
    }
    
    const setupTarget = () => {

        // set file from supplied requiem file target if applciable
        if(fileTarget) {
            setFile({
                file_name: fileTarget.name,
                file_type: fileTarget.type,
                raw: fileTarget
            });
        }

        // set edits for new and existing targets
        setEdits({
            ai_enabled: abstract.object.ai_enabled,
            analyze_communication_style: abstract.object.metadata?.analyze_communication_style,
            description: abstract.object.metadata?.description,
            name: abstract.object.name,
            user_id: abstract.object.user_id
        });
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={resource.id}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium',
            support: getSupportConfig(),
        }}>
           {getContent()}
        </Layer>
    )
}

export const DataVault = React.forwardRef(({ accessGrant, onItemClick, onSelectionChange, selections, utils }, ref) => {

    const [loading, setLoading] = useState(true);
    const [map, setMap] = useState([]);
    const [searchText, setSearchText] = useState(null);
    const [selected, setSelected] = useState(selections || []);
    const { sockets } = useDataStore();

    const onAddNewItemClick = async () => {
        try {

            // verify that user has access to this feature
            await utils.user.capabilities.verify('data_vault', 'create');

            // show layer with options to add an account
            utils.layer.open({
                abstract: Abstract.create({
                    object: DataVaultItem.new(),
                    type: 'data_vault_items'
                }),
                Component: AddEditDataVaultItem.bind(this, { isNewTarget: true }),
                id: 'new_data_vault_item'
            });
            
        } catch(e) {
            console.error(e.message);
        }
    }

    const onDeleteNodeClick = node => {
        utils.alert.show({
            title: 'Delete Item',
            message: `Are you sure that you want to delete "${node.text}" from your Data Vault? ${node.droppable ? 'This will delete everything within this folder and can not be undone' : 'This can not be undone'}.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onDeleteNodeConfirm.bind(this, node), 500);
                    return;
                }
            }
        });
    }

    const getFolderItemsRecursive = target => {
        return map.reduce((array, node) => {
            if(node.parent === target.id) {
                array.push(node);
                if(node.droppable) {
                    let results = getFolderItemsRecursive(node);
                    array = array.concat(results);
                }
            }
            return array;
        }, []);
    }

    const onDeleteNodeConfirm = async node => {
        try {

            // prepare list of targets for request
            let targets = [node];
            if(node.droppable) {
                targets = getFolderItemsRecursive(node);
                targets.push(node);
            }

            // update values for map locally and with server
            let payload = map.filter(item => targets.find(target => target.id === item.id) ? false : true);
            await onSubmitVaultMapChanges(payload);

            // loop through targets and send requests to server
            for await (const target of targets) {
                if(target.droppable === false) {
                    await Request.post(utils, '/data_vault/', {
                        id: target.id,
                        type: 'delete'
                    });
                }
            }
            
        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue deleting your Data Vault item. ${e.message || 'An unknown error occurred'}`,
                origin: 'data_vault.on_delete_node_confirm'
            });
        }
    }

    const onDrop = async data => {
        try {
            await onSubmitVaultMapChanges(data);
        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue saving your data vault configuration. ${e.message || 'An unknown error occurred'}`,
                origin: 'data_vault.on_drop'
            });
        }
    }

    const onNewFolderClick = async () => {
        try {

            // add folder entry to local map
            let payload = update(map, {
                $push: [{
                    droppable: true,
                    id: `${moment().unix()}.${Utils.randomString(10)}`,
                    parent: 0,
                    text: 'Untitled Folder'
                }]
            });

            // show confirmation banner that folder was added
            utils.banner.show({
                title: 'New Folder Added',
                message: 'A new folder has been added to your Data Vault. You can drag and drop items into this folder to keep your vault organized.',
            });

            // update local state and send payload to server
            await onSubmitVaultMapChanges(payload);

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue saving your data vault configuration. ${e.message || 'An unknown error occurred'}`,
                origin: 'data_vault.on_new_folder_click'
            });
        }
    }

    const onNodeDataChange = async node => {
        try {

            // update values for map locally and with server
            let payload = map.map(entry => entry.id === node.id ? node : entry);
            await onSubmitVaultMapChanges(payload);

            // send request to server with updated node data if applicable
            if(node.data?.item?.id) {
                await Request.post(utils, '/data_vault/', {
                    ai_enabled: node.data.item.ai_enabled,
                    description: node.data.item.metadata.description,
                    id: node.id,
                    name: node.text,
                    type: 'update'
                });
            }

        } catch(e) {
            utils.alert.error({
                error: e,
                message: `There was an issue saving your data vault configuration. ${e.message || 'An unknown error occurred'}`,
                origin: 'data_vault.on_node_data_change'
            });
        }
    }

    const onRenderItem = (node, options) => {
        if(accessGrant) {
            return (
                <DataVaultItemNode
                {...options}
                editable={false}
                isLastItem={false/*index === map.length - 1*/}
                multiSelectEnabled={isMultiSelectEnabled()}
                node={node} 
                onItemClick={onItemClick}
                onSelectNode={onSelectNode} 
                selected={selected.includes(node.id)}
                utils={utils} />
            )
        }
        return (
            <DataVaultItemNode
            {...options}
            isLastItem={false/*index === map.length - 1*/}
            multiSelectEnabled={isMultiSelectEnabled()}
            node={node} 
            onDataChange={onNodeDataChange}
            onDeleteNodeClick={onDeleteNodeClick.bind(this, node)}
            onSelectNode={onSelectNode} 
            selected={selected.includes(node.id)}
            utils={utils} />
        )
    }

    const onRenderItemPreview = props => {
        return <DataVaultItemNodeDragPreview {...props} />
    }

    const onRequiemStatusChange = evt => {
        try {
            if(evt.category === 'data_vault_items') {
                setMap(targets => {
                    return targets.map(item => { 
                        if(item.id === evt.id) {
                            item.data.item.requiem_processing_status = evt.status_code;
                        }
                        return item;
                    })
                });
            }
        } catch(e) {
            console.error(e.message);
        }
    }

    const onSelectNode = (node, isMultiSelect = false) => {

        // return single selection if multi-select is not enabled   
        if(isMultiSelectEnabled() === false || isMultiSelect === false) {
            setSelected(node ? [node.id] : []);
            return;
        }

        // update local selection state based on multi-select logic
        setSelected(selected => selected.includes(node.id) ? selected.filter(id => id !== node.id) : [...selected, node.id]);
    }

    const onSubmitVaultMapChanges = async payload => {
        return new Promise(async (resolve, reject) => {
            try {

                // update local state with new payload
                setMap(payload);

                // send request to server with updated data map
                await Request.post(utils, '/data_vault/', {
                    map: payload,
                    type: 'update_map'
                });
                resolve();

            } catch(e) {
                reject(e);
            }
        });
    }

    const getItems = () => {
        if(loading === true) {
            return (
                <div 
                className={'text-left text-xl-center'}
                style={{
                    alignItems: 'center',
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1250,
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 45,
                        width: 45
                    }}/>
                </div>
            )
        }
        if(map.length === 0) {
            return (
                <div 
                className={'text-left text-xl-center'}
                style={{
                    alignItems: 'center',
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    maxWidth: 1250,
                    width: '100%'
                }}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        ...Utils.isMobile() === true && { 
                            border: 'none',
                        },
                        padding: 16,
                        width: '100%'
                    }}>
                        <div style={{
                            alignItems: 'flex-start',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            minWidth: 0
                        }}>
                            <span style={{
                                ...Appearance.textStyles.header(),
                                fontFamily: 'Zen Dots'
                            }}>{'No Data Vault Items Found'.toUpperCase()}</span>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                color: Appearance.colors.subText(),
                                whiteSpace: 'normal'
                            }}>{'We were unable to locate any data vault items for your profile.'}</span>
                        </div>
                    </div>
                </div>
            )
        }
        return (
            <div style={{
                ...Utils.isMobile() === false && Appearance.styles.unstyledPanel(),
                width: '100%'
            }}>
                <Tree
                classes={{ 
                    root: 'data-vault-list',
                    container: 'data-vault-list-item' 
                }}
                onDrop={onDrop}
                dragPreviewRender={onRenderItemPreview}
                render={onRenderItem} 
                rootId={0}
                sort={(a,b) => {
                    
                    // put all folders with no name or named Untitled Folder at the top of the list
                    if(a.droppable === true && (a.text === '' || a.text.toLowerCase() === 'untitled folder')) {
                        return -1;
                    }
                    if(b.droppable === true && (b.text === '' || b.text.toLowerCase() === 'untitled folder')) {
                        return 1;
                    }

                    // soft by folders alphabetically
                    if(a.droppable === true && b.droppable === true) {
                        return a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1;
                    }

                    // sort by name alphabetically
                    return a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1;
                }}
                tree={map} />
            </div>
        )
    }

    const getSearchField = () => {
        return null; (
            <div style={{
                ...Utils.isMobile() === true && {
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    padding: Appearance.styles.panelPadding()
                },
                ...Utils.isMobile() === false && {
                    marginBottom: 24
                },
                width: '100%'
            }}>
                <TextField
                icon={'search'}
                loading={loading === 'search'}
                onChange={text => {
                    setLoading('search');
                    setSearchText(text);
                }}
                placeholder={'Search by vault item name...'}
                useDelay={true}
                value={searchText} />
            </div>
        )
    }

    const connectToWebsockets = () => {
        sockets.on('requiem', 'processing_status_change', onRequiemStatusChange);
        return () => {
            sockets.off('requiem', 'processing_status_change', onRequiemStatusChange);
        }
    }

    const isMultiSelectEnabled = () => {
        return typeof(onSelectionChange) === 'function';
    }

    const setupVaultItems = async () => {
        try {

            // determine if an access grant with a public config was provided
            if(accessGrant) {

                // prepare items, map, and item keys
                let { items, map: mapItems } = accessGrant.data.share_data_vault_items || {};
                let keys = mapItems.map(entry => entry.id);
                
                // end loading and update local state, items without an whitelisted parent are placed at the root
                setLoading(false);
                setMap(mapItems.map(entry => {
                    return {
                        ...entry,
                        item: items.find(item => item.id === entry.id),
                        parent: keys.includes(entry.parent) ? entry.parent : 0
                    }
                }));
                return;
            }

            // fetch vault items from the server
            let { map } = await Request.get(utils, '/data_vault/', { type: 'items' });

            // end loading and update local state
            setLoading(false);
            setMap(map);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving your data vault items. ${e.message || 'An unknown error occurred'}`,
                origin: 'data_vault.setup_vault_items'
            });
        }
    }

    useImperativeHandle(ref, () => ({
        icon: {
            get: getNodeIcon
        },
        new: {
            folder: onNewFolderClick,
            item: onAddNewItemClick
        },
        nodes: {
            get: ids => {
                return map.filter(node => ids.includes(node.id));
            }
        }
    }), [map]);

    useEffect(() => {
        if(typeof(onSelectionChange) === 'function') {
            onSelectionChange(selected);
        }
    }, [selected]);

    useEffect(() => {

        setupVaultItems();
        let disconnect = connectToWebsockets();

        utils.content.subscribe('data_vault_items', ['data_vault_items'], {
            onFetch: setupVaultItems
        });
        return () => {
            disconnect();
            utils.content.unsubscribe('data_vault_items');
        }
    }, []);

    return (
        <DndProvider 
        backend={MultiBackend} 
        options={getBackendOptions({
            touch: {
                delayTouchStart: 500
            }
        })}>
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '100%'
            }}>
                {getSearchField()}
                {getItems()}
            </div>
        </DndProvider>
    )
});

export const DataVaultPanel = ({ utils }) => {

    const dataVaultRef = useRef(null);
    const { system } = useDataStore();

    const getContainerButtons = () => {
        return [{
            color: 'dark',
            id: 'new_folder',
            onClick: () => dataVaultRef.current?.new.folder(),
            text: 'New Folder'
        },{
            color: 'primary',
            id: 'add_item',
            onClick: () => dataVaultRef.current?.new.item(),
            text: 'Upload File'
        }];
    }

    const getFeatureCards = () => {

        let items = [{
            body: 'All files stored in your Data Vault are encrypted and protected behind your LifeWipe login. Your data stays private, secure, and under your control at all times.',
            title: 'Secure by Design'
        },{
            body: 'Easily upload, manage, and organize important documents in one central location. Access your files anytime through your LifeWipe dashboard, no matter where you are.',
            title: 'Organized & Accessible'
        },{
            body: 'Set custom rules for your files based on specific triggers like inactivity, verified emergencies, or end-of-life events. Choose whether to share, delete, or preserve each document according to your wishes.',
            title: 'Trigger-Based Control'  
        }];
        
        return (
            <div 
            className={'row mx-0'} 
            style={{ 
                justifyContent: 'center' 
            }}>
                {items.map((item, idx) => (
                    <div 
                    key={idx} 
                    className={`col-12 p-0 pb-2`}>
                        <div style={{
                            backgroundColor: Appearance.colors.panelBackground(),
                            border: `2px solid ${Appearance.colors.divider()}`,
                            borderRadius: 12,
                            height: '100%',
                            padding: 24,
                            textAlign: 'left'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.panelSubHeaderText(),
                                display: 'block',
                                fontSize: 16,
                                marginBottom: 8
                            }}>{item.title.toUpperCase()}</span>
                            <span style={{
                                ...Appearance.textStyles.defaultText()
                            }}>{item.body}</span>
                        </div>
                    </div>
                ))}
            </div>
        )
    }
    
    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%'
        }}>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            useContentDivider={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`The Secure Data Vault is your private space to store important digital documents—safely and accessibly. All items are encrypted and protected behind your LifeWipe login, with the option to update or remove them anytime. You can also set custom instructions to automatically share or destroy specific vault contents based on triggers like inactivity, death, or verified emergencies.`}
                leftContent={(
                    <video
                    autoPlay={true}
                    loop={true}
                    muted={true}
                    playsInline={true}
                    style={{
                        height: '100%',
                        objectFit: 'cover',
                        width: '100%'
                    }}>
                        <source 
                        src={"images/secure-data-vault.mp4"}
                        type={"video/mp4"} />
                    </video>
                )}
                title={'Secure Data Vault'} />
            </AnimatedElement>
            <AnimatedElement
            {...utils.animations.get('panel')}
            animateWithPage={true}
            style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <PanelContentContainer
                body={`This is where you organize the contents of your Secure Data Vault. You can upload important documents, create folders for clarity, and update or remove items anytime. Whether you're storing personal notes, legal files, or sensitive records, everything remains encrypted and under your control. You can also prepare certain files for future sharing by assigning them to trusted contacts or linking them to specific triggers — but nothing moves unless you say so.`}
                buttons={getContainerButtons()}
                title={'My Files and Folders'}>
                    <div style={{
                        ...system.window.isMobile && Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        <DataVault 
                        ref={dataVaultRef}
                        utils={utils} />
                    </div>
                </PanelContentContainer>
            </AnimatedElement>
        </div>
    )
}

export const DataVaultItemNode = ({ depth, editable = true, isDropTarget, isLastItem = false, isOpen, multiSelectEnabled = false, node, onDataChange, onDeleteNodeClick, onItemClick, onSelectNode, onToggle, selected, utils }) => {
    
    const clickTimeout = useRef(null);
    const [edits, setEdits] = useState(null);
    const [editing, setEditing] = useState(false);
    const indent = Utils.isMobile() === true ? 16 : 36;

    const onClearEdits = () => {
        setEditing(false);
        setEdits(null);
    }

    const onDeleteClick = evt => {
        evt.stopPropagation();
        onDeleteNodeClick();
    }

    const onFieldClick = evt => {

        // stop event propagation and prepare time
        evt.stopPropagation();

        // check for multi-select (Command key on Mac, Ctrl key on Windows/Linux)
        let isMultiSelect = multiSelectEnabled === true && (evt.metaKey || evt.ctrlKey) ? true : false;

        // select node if node isn't already selected or if more than 1.5 seconds have passed since original selection
        if(selected === false || isMultiSelect === true) {
            onSelectNode(node, isMultiSelect);
            return;
        }

        // clear existing timeout if applicable
        if(clickTimeout.current) {
            clearTimeout(clickTimeout.current);
            clickTimeout.current = null;
        }

        // set new click timeout for editing (only if not multi-selecting)
        if(editable === true && isMultiSelect === false) {
            clickTimeout.current = setTimeout(setEditing.bind(this, editable), 500);
        }
    }

    const onItemContainerClick = evt => {
        evt.stopPropagation();
        onSelectNode(node, multiSelectEnabled === true && (evt.metaKey || evt.ctrlKey));
    }

    const onItemDoubleClick = evt => {

        // clear existing click timeout if applicable
        if(clickTimeout.current) {
            clearTimeout(clickTimeout.current);
            clickTimeout.current = null;
        }

        // no further logic is required if editing is enabled
        if(editing === true) {
            return;
        }

        // stop event propagation and set selection with parent
        evt.stopPropagation();
        onSelectNode(node, false);

        // trigger a toggle for a folder if applicable
        if(node.droppable === true) {
            onToggleClick(evt);
            return;
        }

        // determine if a callback was provided for item interactions
        if(typeof(onItemClick) === 'function') {
            onItemClick(node.data.item, evt);
            return;
        }
        
        // open document editor for account owner
        utils.layer.open({
            abstract: Abstract.create({
                object: DataVaultItem.create(node.data.item),
                type: 'data_vault_items'
            }),
            Component: AddEditDataVaultItem.bind(this, { 
                isNewTarget: false 
            }),
            id: `data_vault_items.${node.data.item.id}`
        });
    }

    const onSaveEdits = () => {
        setEditing(false);
        onDataChange({ 
            ...node, 
            ...edits,
            ...edits?.text?.length === 0 && {
                text: node.droppable ? 'Untitled Folder' : 'Untitled File'
            } 
        });
    }

    const onRequiemClick = evt => {
        evt.stopPropagation();
        utils.alert.show({
            title: 'Requiem AI Enabled',
            message: 'Requiem is an optional AI feature that uses your Secure Data Vault uploads—like letters, reflections, and personal documents—to build a digital likeness of you. It allows your voice, tone, and values to be experienced through an AI model trained on your own words. Designed for memory, guidance, or closure, Requiem helps you leave behind more than just data—it helps you leave presence.'
        });
    }

    const onToggleClick = evt => {
        evt.stopPropagation();
        onToggle(node.id);
    };

    const getAccessoryButtons = () => {
        if(editing === true) {
            return (
                <>
                <img
                className={'disable-text-selection text-button'}
                onClick={onClearEdits}
                src={'images/red-x-icon.png'}
                style={{
                    height: 25,
                    marginLeft: 12,
                    objectFit: 'contain',
                    width: 25
                }} />
                <img
                className={'disable-text-selection text-button'}
                onClick={onSaveEdits}
                src={'images/checkmark-button-green-small.png'}
                style={{
                    height: 25,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 25
                }} />
                </>
            )
        }
        return (
            <>
            {node.data?.item?.requiem_processing_status === 2 && (
                <img
                className={'disable-text-selection text-button rotate-animation'}
                onClick={onRequiemClick}
                src={'images/requiem-processing-icon-green.png'}
                style={{
                    height: 25,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 25
                }} />
            )}
            {editable === true && (
                <img
                className={'disable-text-selection text-button'}
                onClick={onDeleteClick}
                src={'images/delete-red-icon.png'}
                style={{
                    height: 25,
                    marginLeft: 8,
                    objectFit: 'contain',
                    width: 25
                }} />
            )}
            </>
        )
    }

    const getLabelComponent = () => {
        if(editing === false) {
            return (
                <span 
                className={'disable-text-selection'}
                onClick={onFieldClick}
                style={{
                    ...Appearance.textStyles.defaultText(),
                    ...selected === true && {
                        color: Appearance.colors.primary()
                    },
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textAlign: 'left',
                    textOverflow: 'ellipsis'
                }}>{node.text || 'Untitled'}</span>
            )
        }
        return (
            <TextField 
            autoHighlight={true}
            onChange={text => setEdits({ text })}
            onKeyDown={keyCode => {
                if(keyCode === 13) {
                    onSaveEdits();
                }
                if(keyCode === 27) {
                    onClearEdits();
                }
            }}
            placeholder={'Untitled'}
            value={node.text} />
        )
    }

    return (
        <div 
        className={`view-entry ${window.theme}`}
        onClick={onItemContainerClick}
        onDoubleClick={onItemDoubleClick}
        style={{ 
            alignItems: 'center',
            borderBottom: isLastItem === false && Appearance.styles.bottomBorder(),
            display: 'flex',
            flexDirection: 'row',
            minWidth: 0,
            padding: 16,
            paddingInlineStart: (depth + 1) * indent,
            position: 'relative',
            WebkitTouchCallout: 'none',
            width: '100%',
            ...isDropTarget && {
                backgroundColor: Appearance.colors.divider()
            }
        }}>
            {node.droppable && (
                <div 
                className={'cursor-pointer'}
                onClick={onToggleClick}
                style={{
                    left: 14 + (depth * indent),
                    position: 'absolute',
                    transform: `rotate(${isOpen ? 90 : 0}deg)`,
                    transition: 'transform linear .1s'
                }}>
                    <img
                    src={window.theme === 'dark' ? 'images/next-arrow-white-small.png' : 'images/next-arrow-grey-small.png'}
                    style={{
                        height: 12,
                        objectFit: 'contain',
                        width: 12
                    }} />
                </div>
            )}
            <img
            draggable={false}
            src={getNodeIcon(node)}
            style={{
                flex: 'none',
                height: Utils.isMobile() ? 30 : 35,
                marginLeft: 18,
                marginRight: 16,
                objectFit: 'contain',
                opacity: node.droppable && node.text?.toLowerCase() === 'untitled folder' ? 0.5 : 1,
                width: Utils.isMobile() ? 30 : 35
            }} />
            <div style={{
                flexGrow: 1,
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',
                textAlign: 'left',
                textOverflow: 'ellipsis'
            }}>
                {getLabelComponent()}
            </div>
            {getAccessoryButtons()}
        </div>
    );
};

export const DataVaultItemNodeDragPreview = ({ item, isDragging }) => {

    let size = Utils.isMobile() ? 100 : 35;
    return isDragging && (
        <img 
        src={getNodeIcon(item)}
        style={{
            height: size,
            left: -(size / 2),
            objectFit: 'contain',
            position: 'absolute',
            top: Utils.isMobile() ? (-size) : -(size / 2), 
            width: size,
        }} />
    )
}

export const getNodeIcon = node => {
    if(node.droppable === true) {
        if(node.icon) {
            return window.theme === 'dark' ? `images/${node.icon}-dark.png` : `images/${node.icon}-light.png`;
        }
        return window.theme === 'dark' ? 'images/data-vault-item-folder-white.png' : 'images/data-vault-item-folder-grey.png';
    }
    switch(node.data?.item?.metadata?.mime_type) {

        case 'image/jpeg':
        case 'image/png':
        case 'image/gif':
        case 'image/webp':
        case 'image/svg+xml':
        case 'image/bmp':
        case 'image/tiff':
        case 'image/x-icon':
        case 'image/heic':
        case 'image/heif':
        return window.theme === 'dark' ? 'images/data-vault-item-image-white.png' : 'images/data-vault-item-image-grey.png';

        case 'video/mp4':
        case 'video/webm':
        case 'video/ogg':
        case 'video/quicktime':
        case 'video/x-msvideo':
        case 'video/x-matroska':
        case 'video/x-flv':
        case 'video/x-m4v':
        case 'video/3gpp':
        case 'video/mp2t':
        return window.theme === 'dark' ? 'images/data-vault-item-video-white.png' : 'images/data-vault-item-video-grey.png';

        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'application/vnd.oasis.opendocument.spreadsheet':
        case 'application/vnd.google-apps.spreadsheet':
        return window.theme === 'dark' ? 'images/data-vault-item-xls-white.png' : 'images/data-vault-item-xls-grey.png';

        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'application/vnd.apple.pages':
        return window.theme === 'dark' ? 'images/data-vault-item-doc-white.png' : 'images/data-vault-item-doc-grey.png';

        case 'application/pdf':
        return window.theme === 'dark' ? 'images/data-vault-item-pdf-white.png' : 'images/data-vault-item-pdf-grey.png';

        case 'text/csv':
        case 'text/plain':
        case 'text/markdown':
        case 'text/richtext':
        case 'text/x-markdown':
        case 'application/rtf':
        return window.theme === 'dark' ? 'images/data-vault-item-txt-white.png' : 'images/data-vault-item-txt-grey.png';

        default:
        return window.theme === 'dark' ? 'images/data-vault-item-general-white.png' : 'images/data-vault-item-general-grey.png';
    }
}

export const RequiemDataVaultItemEditor = ({ abstract, index, options, utils }) => {

    const layerID = `requiem_data_vault_item_editor_${abstract.getID()}`;
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState({});
    const [hasMadeEdits, setHasMadeEdits] = useState(false);
    const [layerState, setLayerState] = useState([]);
    const [loading, setLoading] = useState(true);

    const onCloseClick = () => {
        if(hasMadeEdits === false) {
            setLayerState('close');
            return;
        }
        utils.alert.show({
            title: 'Unsaved Changes',
            message: `It looks like you have some unsaved changes. Would you like to save these changes before closing this window?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Do Not Save Changes',
                style: 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    setTimeout(onSubmitClick, 500);
                    return;
                }
                setLayerState('close');
            }
        });
    }

    const onItemClick = (entryIndex, itemIndex, evt) => {
        evt.stopPropagation();
        setEditing(prev => {
            if(prev?.entryIndex === entryIndex && prev?.itemIndex === itemIndex) {
                return null;
            }
            return { entryIndex, itemIndex }
        });
    }

    const onSubmitClick = async () => {
        try {

            setLoading('submit');
            await Request.post(utils, '/requiem/', {
                category: abstract.type,
                content: data.content,
                id: abstract.getID(),
                type: 'update_vector_data_for_resource'
            });

            setLoading(false);
            setLayerState('close'); 

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue saving your changes. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_data_vault_item_editor.on_submit_click'
            });
        }
    }

    const onUpdateBodyText = (entryIndex, text) => {
        
        // set flag for unsaved edits
        setHasMadeEdits(true);

        // update state for data entry
        setData(prev => {
            return update(prev, {
                content: {
                    [entryIndex]: {
                        body: {
                            $set: text
                        }
                    }
                }
            });
        });
    }

    const onUpdateItem = (entryIndex, itemIndex, text) => {

        // set flag for unsaved edits
        setHasMadeEdits(true);

        // update state for data entry
        setData(prev => {
            return update(prev, {
                content: {
                    [entryIndex]: {
                        items: {
                            [itemIndex]: { 
                                $set: text 
                            }
                        }
                    }
                }
            });
        });
    }

    const getButtons = () => {
        return [{
            color: 'dark',
            id: 'close',
            onClick: onCloseClick,
            text: 'Close'
        },{
            color: 'primary',
            id: 'submit',
            onClick: onSubmitClick,
            text: 'Save Changes',
            visible: hasMadeEdits
        }];
    }

    const getContent = () => {
        return data?.content?.map((entry, index, items) => {
            if(entry.is_list === false) {

                let isEditing = editing?.entryIndex === index && editing?.itemIndex === 'body';
                if(isEditing === true) {
                    return (
                        <LayerItem
                        collapsed={null}
                        key={index}
                        title={entry.title}>
                            <div style={{
                                ...Appearance.styles.unstyledPanel(),
                                padding: '12px 16px 12px 16px',
                                width: '100%'
                            }}>
                                <TextView
                                onChange={text => onUpdateBodyText(index, text)}
                                fieldStyle={{
                                    height: 375
                                }}
                                value={entry.body} />
                                <Button 
                                color={'primary'}
                                containerStyle={{
                                    marginTop: 12
                                }}
                                label={'Done'}
                                onClick={setEditing.bind(this, {})}
                                type={'large'} />
                            </div>
                        </LayerItem>
                    )
                }
                return (
                    <LayerItem
                    collapsed={null}
                    key={index}
                    title={entry.title}>
                        <div 
                        className={`view-entry ${window.theme}`}
                        onClick={onItemClick.bind(this, index, 'body')}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            padding: '12px 16px 12px 16px',
                            width: '100%'
                        }}>
                            <span style={{
                                ...Appearance.textStyles.defaultText(),
                                display: 'block'
                            }}>{entry.body}</span>
                        </div>
                    </LayerItem>
                )
            }
            return (
                <LayerItem
                collapsed={null}
                key={index}
                lastItem={index === items.length - 1}
                title={entry.title}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {entry.items.map((item, i) => {
                            let isEditing = editing?.entryIndex === index && editing?.itemIndex === i;
                            if(isEditing === true) {
                                return (
                                    <div 
                                    key={i}
                                    style={{
                                        borderBottom: i !== entry.items.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                                        padding: 16,
                                        width: '100%'
                                    }}>
                                        <TextView
                                        onChange={text => onUpdateItem(index, i, text)} 
                                        value={item} />
                                        <Button 
                                        color={'primary'}
                                        containerStyle={{
                                            marginTop: 12
                                        }}
                                        label={'Done'}
                                        onClick={setEditing.bind(this, {})}
                                        type={'large'} />
                                    </div>
                                )
                            }
                            return (
                                <div 
                                className={`view-entry ${window.theme}`}
                                key={i}
                                onClick={onItemClick.bind(this, index, i)}
                                style={{
                                    borderBottom: i !== entry.items.length - 1 && `1px solid ${Appearance.colors.divider()}`,
                                    padding: '12px 16px 12px 16px',
                                    width: '100%'
                                }}>
                                    <span style={{
                                        ...Appearance.textStyles.defaultText(),
                                        display: 'block'
                                    }}>{item}</span>
                                </div>
                            )
                        })}
                    </div>
                </LayerItem>
            )
        });
    }

    const fetchRequiemData = async () => {
        try {

            setLoading(true);
            let { data } = await Request.get(utils, '/requiem/', {
                category: abstract.type,
                id: abstract.getID(),
                type: 'get_vector_data_for_resource'
            });

            setLoading(false);
            setData(data);

        } catch(e) {
            setLoading(false);
            utils.alert.error({
                error: e,
                message: `There was an issue retrieving the Requiem data for this item. ${e.message || 'An unknown error occurred'}`,
                origin: 'requiem_data_vault_item_editor.fetch_requiem_data'
            });
        }
    }

    useEffect(() => {
        fetchRequiemData();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}
        utils={utils}>
            <LayerItem title={'About Collaboration'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '12px 16px 12px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.defaultText(),
                        display: 'block'
                    }}>{'What you see here is Requiem’s best attempt at turning this file into a clear, easy-to-review overview. Think of it as a strong first draft: if anything looks off, feels incomplete, or could be described more clearly, you can click into it and make changes. The goal is to help you and Requiem shape this information together so it reflects your asset as accurately as possible.'}</span>
                </div>
            </LayerItem>
            {getContent()}
        </Layer>
    )
}